import { c as _c } from "react/compiler-runtime";
import { formatDistanceToNow } from 'date-fns';
import { useContext, useEffect } from 'react';
import NumberFormat from 'react-number-format';
import { useTheme } from 'styled-components';
import { APIDestination } from '../../../../../../../typings/Destination.interface';
import { BackpressureIssueWithData } from '../../../../../../../typings/Issue.interface';
import { formatAmountOfTime } from '../../../../utils';
import { Granularity } from '../../../../utils/formatMetricsQuery';
import { StyledCard } from '../../../common/base/Card';
import Skeleton from '../../../common/base/Skeleton';
import Text from '../../../common/base/Text';
import { ChartDataSet } from '../../../common/Chart/Chart';
import ChartInteractionGroup from '../../../common/Chart/ChartInteractionGroup';
import { Div } from '../../../common/helpers/StyledUtils';
import { MetricOptions } from '../../../common/metrics/metric-definitions';
import DeliveryGroupMetricChart, { useMetricChartData } from '../Metrics/MetricChart';
import MetricChart from '../../../common/metrics/MetricChart';
import { ChartData, useMetric } from '../Metrics/useMetric';
import { DashboardContext } from '../DashboardContext';
import Icon from '../../../common/base/Icon';
import Dropdown from '../../../common/Dropdown';
import ChangeRateLimit from './ChangeRateLimit';
import useSearchQuery from '../../../hooks/useSearchQuery';
import Button from '../../../common/base/Button';
import Tooltip from '../../../common/base/Tooltip';
import LINKS from '../../../../configs/links';
import { getDestinationDeliveryPolicy } from '../Connections/Forms/destination_configuration';
import MetricDatePicker, { useDateRange } from '../Metrics/MetricDatePicker';
import { useRefreshKey } from '../Metrics/RefreshKeyContext';
const toDataset = (data: ChartData | null | undefined, config: {
  key: string;
  label: string;
  sort_order?: number;
  metric_type: ChartDataSet['metric_type'];
  theme_color?: ChartDataSet['theme_color'];
  hex_color?: string;
  getDataLabel?: ChartDataSet['getDataLabel'];
}): ChartDataSet | null => {
  if (!data) {
    return null;
  }
  return {
    ...data,
    ...config
  };
};
const rateLimitToGranularity = (granularity: Granularity, destination?: APIDestination): number => {
  if (!destination) {
    return 0;
  }
  const delivery_policy = getDestinationDeliveryPolicy(destination);
  if (!delivery_policy?.rate) {
    return 0;
  }
  if (delivery_policy.period === 'second' && granularity === 'minute') {
    return delivery_policy.rate * 60;
  }
  if (delivery_policy.period === 'second' && granularity === 'hour') {
    return delivery_policy.rate * 60 * 60;
  }
  if (delivery_policy.period === 'minute' && granularity === 'hour') {
    return delivery_policy.rate * 60;
  }
  if (delivery_policy.period === 'hour' && granularity === 'minute') {
    return Math.round(delivery_policy.rate / 60);
  }
  return delivery_policy.rate;
};
export function useBackpressureChartsDefinition(destination, refresh_key) {
  const $ = _c(86);
  const theme = useTheme();
  const {
    query,
    updateSearchQuery
  } = useSearchQuery();
  const [date_range, onDateRangeChanged] = useDateRange("last_hour");
  const t0 = destination?.id || "";
  let t1;
  if ($[0] !== t0) {
    t1 = {
      filters: {
        destination_id: [t0]
      }
    };
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = t1 satisfies MetricOptions;
  let t3;
  if ($[2] !== date_range || $[3] !== t2) {
    t3 = {
      options: t2,
      date_range,
      force_rate_as: "minute"
    };
    $[2] = date_range;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const {
    data: data_input,
    error: input_error,
    is_loading: input_loading
  } = useMetric("chart:line", "events_pending_rate", refresh_key, t3);
  const t4 = destination?.id || "";
  let t5;
  if ($[5] !== t4) {
    t5 = {
      filters: {
        destination_id: [t4]
      }
    };
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const t6 = t5 satisfies MetricOptions;
  let t7;
  if ($[7] !== date_range || $[8] !== t6) {
    t7 = {
      options: t6,
      date_range,
      force_rate_as: "minute"
    };
    $[7] = date_range;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const {
    data: data_output,
    error: output_error,
    is_loading: output_loading
  } = useMetric("chart:line", "attempts_delivered_rate", refresh_key, t7);
  const expanded_height = window.innerHeight - 101;
  const rate_limit_period = destination ? getDestinationDeliveryPolicy(destination)?.period : undefined;
  const t8 = destination?.id || "";
  let t9;
  if ($[10] !== t8) {
    t9 = {
      filters: {
        destination_id: [t8]
      }
    };
    $[10] = t8;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  const t10 = t9 satisfies MetricOptions;
  let t11;
  if ($[12] !== date_range || $[13] !== t10) {
    t11 = {
      options: t10,
      date_range
    };
    $[12] = date_range;
    $[13] = t10;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  const latency_metric_configs = t11;
  const {
    data: average_latency,
    error: average_latency_error
  } = useMetric("chart:line", "attempts_average_response_time", refresh_key, latency_metric_configs);
  const {
    data: p95_latency,
    error: p95_latency_error
  } = useMetric("chart:line", "attempts_95_response_time", refresh_key, latency_metric_configs);
  const {
    data: p99_latency,
    error: p99_latency_error
  } = useMetric("chart:line", "attempts_99_response_time", refresh_key, latency_metric_configs);
  const {
    data: max_latency,
    error: max_latency_error
  } = useMetric("chart:line", "attempts_max_response_time", refresh_key, latency_metric_configs);
  const latency_loading = average_latency === undefined || p95_latency === undefined || p99_latency === undefined || max_latency === undefined;
  const formatLatency = _temp;
  let t12;
  if ($[15] !== average_latency || $[16] !== latency_loading || $[17] !== max_latency || $[18] !== p95_latency || $[19] !== p99_latency || $[20] !== theme) {
    t12 = latency_loading ? null : [toDataset(max_latency, {
      key: "max",
      label: "Max",
      sort_order: 3,
      metric_type: "average",
      hex_color: theme.colors.surface.chart.blue_600,
      getDataLabel: formatLatency
    }), toDataset(p99_latency, {
      key: "p99",
      label: "99th",
      sort_order: 2,
      metric_type: "average",
      hex_color: theme.colors.surface.chart.blue_500,
      getDataLabel: formatLatency
    }), toDataset(p95_latency, {
      key: "p95",
      label: "95th",
      sort_order: 1,
      metric_type: "average",
      hex_color: theme.colors.surface.chart.blue_400,
      getDataLabel: formatLatency
    }), toDataset(average_latency, {
      key: "average",
      label: "Avg",
      sort_order: 0,
      metric_type: "average",
      hex_color: theme.colors.surface.chart.blue_300,
      getDataLabel: formatLatency
    })].filter(_temp2);
    $[15] = average_latency;
    $[16] = latency_loading;
    $[17] = max_latency;
    $[18] = p95_latency;
    $[19] = p99_latency;
    $[20] = theme;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  const latency_datasets = t12;
  const t13 = destination?.id || "";
  let t14;
  if ($[22] !== t13) {
    t14 = {
      filters: {
        destination_id: [t13]
      }
    };
    $[22] = t13;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== date_range || $[25] !== t14) {
    t15 = {
      date_range,
      options: t14
    };
    $[24] = date_range;
    $[25] = t14;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== query.expanded || $[28] !== updateSearchQuery) {
    t16 = () => updateSearchQuery({
      expanded: query.expanded ? undefined : "error-rate"
    });
    $[27] = query.expanded;
    $[28] = updateSearchQuery;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  const t17 = query.expanded === "error-rate";
  let t18;
  if ($[30] !== t16 || $[31] !== t17) {
    t18 = {
      hide_total: true,
      onExpand: t16,
      expanded: t17
    };
    $[30] = t16;
    $[31] = t17;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  const error_rate_data = useMetricChartData("attempts_error_rate", t15, t18);
  let t19;
  if ($[33] !== data_input) {
    t19 = data_input && toDataset(data_input, {
      key: "new",
      label: "Pending Events",
      theme_color: "primary",
      metric_type: "rate",
      getDataLabel: _temp3
    });
    $[33] = data_input;
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  let t20;
  if ($[35] !== data_output) {
    t20 = data_output && toDataset(data_output, {
      key: "delivered",
      label: "Delivered Events",
      theme_color: "success",
      metric_type: "rate",
      getDataLabel: _temp4
    });
    $[35] = data_output;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  let t21;
  if ($[37] !== data_output || $[38] !== destination || $[39] !== rate_limit_period) {
    t21 = data_output && rate_limit_period !== "concurrent" && {
      ...data_output,
      key: "limit",
      label: "Max delivery rate",
      theme_color: "danger",
      dashed: true,
      data: data_output.data.map(datum => ({
        ...datum,
        y: rateLimitToGranularity("minute", destination)
      })),
      total: rateLimitToGranularity("minute", destination) * data_output.data.length,
      highest: rateLimitToGranularity("minute", destination),
      average: rateLimitToGranularity("minute", destination),
      getDataLabel: value_2 => `${(Number(value_2) / data_output.time_unit_configs.sampling.value).toLocaleString()} / min`,
      metric_type: "rate"
    } satisfies ChartDataSet;
    $[37] = data_output;
    $[38] = destination;
    $[39] = rate_limit_period;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  let t22;
  if ($[41] !== t19 || $[42] !== t20 || $[43] !== t21) {
    t22 = [t19, t20, t21].filter(_temp5);
    $[41] = t19;
    $[42] = t20;
    $[43] = t21;
    $[44] = t22;
  } else {
    t22 = $[44];
  }
  const event_rate_datasets = t22;
  const t23 = input_loading || output_loading;
  const t24 = !!(input_error || output_error);
  const t25 = query.expanded === "events-rate-over-time" ? "close" : "open";
  const t26 = query.expanded !== "events-rate-over-time";
  const t27 = query.expanded === "events-rate-over-time";
  let t28;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = {
      l: -2
    };
    $[45] = t28;
  } else {
    t28 = $[45];
  }
  let t29;
  if ($[46] !== query.expanded || $[47] !== updateSearchQuery) {
    t29 = () => updateSearchQuery({
      expanded: query.expanded ? undefined : "events-rate-over-time"
    });
    $[46] = query.expanded;
    $[47] = updateSearchQuery;
    $[48] = t29;
  } else {
    t29 = $[48];
  }
  let t30;
  if ($[49] !== t25 || $[50] !== t26 || $[51] !== t27 || $[52] !== t29) {
    t30 = <Button key="open" icon={t25} ghost={t26} outline={t27} m={t28} onClick={t29} />;
    $[49] = t25;
    $[50] = t26;
    $[51] = t27;
    $[52] = t29;
    $[53] = t30;
  } else {
    t30 = $[53];
  }
  const t31 = query.expanded === "events-rate-over-time" ? expanded_height : 250;
  let t32;
  if ($[54] !== event_rate_datasets || $[55] !== t23 || $[56] !== t24 || $[57] !== t30 || $[58] !== t31) {
    t32 = <MetricChart key="events-rate-over-time" title="Event rate over time" title_tooltip="The rate at which events are being delivered to this destination." legend_placeholder="Pending Events" chart_type="line" datasets={event_rate_datasets} loading={t23} error={t24} action={t30} height={t31} />;
    $[54] = event_rate_datasets;
    $[55] = t23;
    $[56] = t24;
    $[57] = t30;
    $[58] = t31;
    $[59] = t32;
  } else {
    t32 = $[59];
  }
  const t33 = !!(average_latency_error || p95_latency_error || p99_latency_error || max_latency_error);
  const t34 = query.expanded === "attempts-response-time" ? "close" : "open";
  const t35 = query.expanded !== "attempts-response-time";
  const t36 = query.expanded === "attempts-response-time";
  let t37;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = {
      l: -2
    };
    $[60] = t37;
  } else {
    t37 = $[60];
  }
  let t38;
  if ($[61] !== query.expanded || $[62] !== updateSearchQuery) {
    t38 = () => updateSearchQuery({
      expanded: query.expanded ? undefined : "attempts-response-time"
    });
    $[61] = query.expanded;
    $[62] = updateSearchQuery;
    $[63] = t38;
  } else {
    t38 = $[63];
  }
  let t39;
  if ($[64] !== t34 || $[65] !== t35 || $[66] !== t36 || $[67] !== t38) {
    t39 = <Button icon={t34} ghost={t35} outline={t36} m={t37} onClick={t38} />;
    $[64] = t34;
    $[65] = t35;
    $[66] = t36;
    $[67] = t38;
    $[68] = t39;
  } else {
    t39 = $[68];
  }
  const t40 = query.expanded === "attempts-response-time" ? expanded_height : 250;
  let t41;
  if ($[69] !== latency_datasets || $[70] !== latency_loading || $[71] !== t33 || $[72] !== t39 || $[73] !== t40) {
    t41 = <MetricChart key="attempts-response-time" title="Response latency" chart_type="line" datasets={latency_datasets} force_legend={true} loading={latency_loading} error={t33} action={t39} height={t40} />;
    $[69] = latency_datasets;
    $[70] = latency_loading;
    $[71] = t33;
    $[72] = t39;
    $[73] = t40;
    $[74] = t41;
  } else {
    t41 = $[74];
  }
  const t42 = query.expanded === "error-rate" ? expanded_height : 250;
  let t43;
  if ($[75] !== error_rate_data || $[76] !== t42) {
    t43 = <MetricChart key="error-rate" {...error_rate_data} height={t42} />;
    $[75] = error_rate_data;
    $[76] = t42;
    $[77] = t43;
  } else {
    t43 = $[77];
  }
  let t44;
  if ($[78] !== t32 || $[79] !== t41 || $[80] !== t43) {
    t44 = {
      "events-rate-over-time": t32,
      "attempts-response-time": t41,
      "error-rate": t43
    };
    $[78] = t32;
    $[79] = t41;
    $[80] = t43;
    $[81] = t44;
  } else {
    t44 = $[81];
  }
  let t45;
  if ($[82] !== date_range || $[83] !== onDateRangeChanged || $[84] !== t44) {
    t45 = {
      charts: t44,
      date_range,
      onDateRangeChanged
    };
    $[82] = date_range;
    $[83] = onDateRangeChanged;
    $[84] = t44;
    $[85] = t45;
  } else {
    t45 = $[85];
  }
  return t45;
}
function _temp5(dataset_0) {
  return !!dataset_0;
}
function _temp4(value_1) {
  return `${Math.round(Number(value_1))} / min`;
}
function _temp3(value_0) {
  return `${Math.round(Number(value_0))} / min`;
}
function _temp2(dataset) {
  return !!dataset;
}
function _temp(value) {
  return `${Math.round(Number(value)).toLocaleString()} ms`;
}
const BackpressureIssue: React.FC<{
  issue: BackpressureIssueWithData;
  mutateIssue: () => void;
}> = t0 => {
  const $ = _c(167);
  const {
    issue,
    mutateIssue
  } = t0;
  const {
    subscription,
    team
  } = useContext(DashboardContext);
  const {
    refresh_key,
    updateRefreshKey
  } = useRefreshKey();
  const now = new Date(refresh_key);
  const last_minute_start = new Date(now.getTime() - 60000).toISOString();
  const retention_start = new Date(now.getTime() - 86400000 * (subscription?.retention_days ?? 0)).toISOString();
  const destination = issue.data.destination;
  let t1;
  if ($[0] !== issue.reference.delivery_groups) {
    t1 = issue.reference.delivery_groups || [];
    $[0] = issue.reference.delivery_groups;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const backpressured_delivery_groups = t1;
  const delivery_group_filter = backpressured_delivery_groups.length === 1 && backpressured_delivery_groups[0] === null ? null : backpressured_delivery_groups;
  const {
    charts: backpressure_charts,
    date_range,
    onDateRangeChanged
  } = useBackpressureChartsDefinition(destination, refresh_key);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = ["delivery_group"];
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== destination.id) {
    t3 = [destination.id];
    $[3] = destination.id;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== delivery_group_filter || $[6] !== t3) {
    t4 = {
      dimensions: t2,
      filters: {
        destination_id: t3,
        delivery_group: delivery_group_filter
      }
    };
    $[5] = delivery_group_filter;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = t4 satisfies MetricOptions;
  let t6;
  if ($[8] !== date_range || $[9] !== t5) {
    t6 = {
      date_range,
      options: t5
    };
    $[8] = date_range;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const delivery_group_metric_configs = t6;
  const {
    data: latest_input,
    refetch: refetchInputTotal
  } = useMetric("card", "events_pending_timeseries_count", refresh_key, {
    options: {
      filters: {
        destination_id: [destination.id]
      }
    } satisfies MetricOptions,
    date_range: {
      min: last_minute_start,
      max: now.toISOString()
    }
  });
  const {
    data: latest_output,
    refetch: refetchOutputTotal
  } = useMetric("card", "attempts_delivered_count", refresh_key, {
    options: {
      filters: {
        destination_id: [destination.id]
      }
    } satisfies MetricOptions,
    date_range: {
      min: last_minute_start,
      max: now.toISOString()
    }
  });
  const {
    data: total_count_pending,
    refetch: refetchTotalCountPending
  } = useMetric("card", "events_pending_timeseries_count", refresh_key, {
    options: {
      filters: {
        destination_id: [destination.id]
      }
    } satisfies MetricOptions,
    date_range: {
      min: retention_start,
      max: now.toISOString()
    }
  });
  let t7;
  if ($[11] !== destination.id) {
    t7 = {
      filters: {
        destination_id: [destination.id]
      }
    };
    $[11] = destination.id;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const t8 = t7 satisfies MetricOptions;
  let t9;
  if ($[13] !== date_range || $[14] !== t8) {
    t9 = {
      options: t8,
      date_range
    };
    $[13] = date_range;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const {
    data: oldest_pending_histogram,
    refetch: refetchOldest
  } = useMetric("chart:line", "events_oldest_pending", refresh_key, t9);
  const oldest_attempt_date = oldest_pending_histogram === undefined ? undefined : Number(oldest_pending_histogram?.data[oldest_pending_histogram.data.length - 1]?.y || 0);
  let t10;
  if ($[16] !== refetchInputTotal || $[17] !== refetchOldest || $[18] !== refetchOutputTotal || $[19] !== refetchTotalCountPending) {
    t10 = () => {
      refetchTotalCountPending();
      refetchOldest();
      refetchInputTotal();
      refetchOutputTotal();
    };
    $[16] = refetchInputTotal;
    $[17] = refetchOldest;
    $[18] = refetchOutputTotal;
    $[19] = refetchTotalCountPending;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== refresh_key) {
    t11 = [refresh_key];
    $[21] = refresh_key;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  useEffect(t10, t11);
  const loading = !(typeof total_count_pending === "number" && total_count_pending >= 0) || !(typeof latest_input === "number" && latest_input >= 0) || !(typeof latest_output === "number" && latest_output >= 0) || typeof total_count_pending !== "number";
  let rate;
  let T0;
  let T1;
  let T2;
  let T3;
  let loadingSkeleton;
  let rate_limit;
  let rate_limit_period;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  if ($[23] !== date_range || $[24] !== destination || $[25] !== latest_input || $[26] !== latest_output || $[27] !== loading || $[28] !== onDateRangeChanged || $[29] !== team || $[30] !== total_count_pending || $[31] !== updateRefreshKey) {
    let backpressure;
    let time_until_resolution;
    if (!loading) {
      rate = latest_output - latest_input;
      backpressure = total_count_pending === 0 ? 0 : Math.round(total_count_pending / latest_output);
      time_until_resolution = backpressure === 0 ? 0 : latest_input > latest_output ? Infinity : total_count_pending === 0 ? 0 : Math.round(total_count_pending / rate);
    }
    let t20;
    if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
      t20 = <Skeleton variant="rectangle" h={{
        px: 24
      }} w={{
        px: 80
      }} loading={true} />;
      $[48] = t20;
    } else {
      t20 = $[48];
    }
    let t21;
    if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
      t21 = <>{t20}<Skeleton variant="rectangle" h={{
          px: 16
        }} w={{
          px: 140
        }} m={{
          t: 1
        }} loading={true} /></>;
      $[49] = t21;
    } else {
      t21 = $[49];
    }
    loadingSkeleton = t21;
    let t22;
    if ($[50] !== destination) {
      t22 = getDestinationDeliveryPolicy(destination);
      $[50] = destination;
      $[51] = t22;
    } else {
      t22 = $[51];
    }
    const delivery_policy = t22;
    rate_limit = delivery_policy?.rate || team.max_events_per_second;
    rate_limit_period = delivery_policy?.period;
    T3 = Div;
    let t23;
    let t24;
    let t25;
    if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
      t23 = {
        justify: "space-between",
        align: "center"
      };
      t24 = {
        b: 4
      };
      t25 = <Text bold={true} size="l" m={0} as="h2">Backpressure metrics</Text>;
      $[52] = t23;
      $[53] = t24;
      $[54] = t25;
    } else {
      t23 = $[52];
      t24 = $[53];
      t25 = $[54];
    }
    let t26;
    if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
      t26 = {
        align: "center",
        gap: 3
      };
      $[55] = t26;
    } else {
      t26 = $[55];
    }
    let t27;
    if ($[56] !== date_range || $[57] !== onDateRangeChanged) {
      t27 = <MetricDatePicker date_range={date_range} onDateRangeChanged={onDateRangeChanged} />;
      $[56] = date_range;
      $[57] = onDateRangeChanged;
      $[58] = t27;
    } else {
      t27 = $[58];
    }
    let t28;
    if ($[59] !== updateRefreshKey) {
      t28 = <Button outline={true} icon="reload" onClick={() => updateRefreshKey()}>Refresh</Button>;
      $[59] = updateRefreshKey;
      $[60] = t28;
    } else {
      t28 = $[60];
    }
    if ($[61] !== t27 || $[62] !== t28) {
      t19 = <Div flex={t23} m={t24}>{t25}<Div flex={t26}>{t27}{t28}</Div></Div>;
      $[61] = t27;
      $[62] = t28;
      $[63] = t19;
    } else {
      t19 = $[63];
    }
    T2 = Div;
    if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
      t18 = {
        t: 4
      };
      $[64] = t18;
    } else {
      t18 = $[64];
    }
    T1 = Div;
    t15 = 100;
    if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
      t16 = {
        columns: 2,
        gap: 6
      };
      $[65] = t16;
    } else {
      t16 = $[65];
    }
    let t29;
    if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
      t29 = {
        y: 3,
        x: 4
      };
      $[66] = t29;
    } else {
      t29 = $[66];
    }
    let t30;
    let t31;
    let t32;
    if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
      t30 = {
        align: "center",
        gap: 1
      };
      t31 = {
        b: 2
      };
      t32 = <Text semi={true}>Projected backpressure</Text>;
      $[67] = t30;
      $[68] = t31;
      $[69] = t32;
    } else {
      t30 = $[67];
      t31 = $[68];
      t32 = $[69];
    }
    let t33;
    if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
      t33 = <Div flex={t30} m={t31}>{t32}<Tooltip tooltip="The estimated time needed to deliver all currently pending events at this destination's delivery rate over the last minute." cta={{
          label: "Learn more in docs",
          to: LINKS.product_docs.issue_triggers_backpressure,
          icon: "docs"
        }}><Icon muted={true} icon="info" /></Tooltip></Div>;
      $[70] = t33;
    } else {
      t33 = $[70];
    }
    const t34 = backpressure === undefined || latest_output === undefined ? loadingSkeleton : <><Text semi={true} center={true} size="3xl">{time_until_resolution <= 0 ? "\u20E0" : backpressure === Infinity ? "\u221E" : formatAmountOfTime(backpressure)}</Text></>;
    if ($[71] !== t34) {
      t17 = <StyledCard p={t29}>{t33}{t34}</StyledCard>;
      $[71] = t34;
      $[72] = t17;
    } else {
      t17 = $[72];
    }
    T0 = StyledCard;
    if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = {
        y: 3,
        x: 4
      };
      $[73] = t12;
    } else {
      t12 = $[73];
    }
    let t35;
    let t36;
    let t37;
    if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
      t35 = {
        align: "center",
        gap: 1
      };
      t36 = {
        b: 2
      };
      t37 = <Text semi={true}>Estimated time to resolution</Text>;
      $[74] = t35;
      $[75] = t36;
      $[76] = t37;
    } else {
      t35 = $[74];
      t36 = $[75];
      t37 = $[76];
    }
    if ($[77] === Symbol.for("react.memo_cache_sentinel")) {
      t13 = <Div flex={t35} m={t36}>{t37}<Tooltip tooltip="The estimated time needed to clear the current pending-event backlog, based on the difference between events entering and leaving the queue over the last minute."><Icon muted={true} icon="info" /></Tooltip></Div>;
      $[77] = t13;
    } else {
      t13 = $[77];
    }
    t14 = time_until_resolution === undefined ? loadingSkeleton : <Text semi={true} center={true} size="3xl">{time_until_resolution <= 0 ? "\u20E0" : time_until_resolution === Infinity ? "\u221E" : formatAmountOfTime(time_until_resolution)}</Text>;
    $[23] = date_range;
    $[24] = destination;
    $[25] = latest_input;
    $[26] = latest_output;
    $[27] = loading;
    $[28] = onDateRangeChanged;
    $[29] = team;
    $[30] = total_count_pending;
    $[31] = updateRefreshKey;
    $[32] = T0;
    $[33] = T1;
    $[34] = T2;
    $[35] = T3;
    $[36] = loadingSkeleton;
    $[37] = rate_limit;
    $[38] = rate_limit_period;
    $[39] = t12;
    $[40] = t13;
    $[41] = t14;
    $[42] = t15;
    $[43] = t16;
    $[44] = t17;
    $[45] = t18;
    $[46] = t19;
    $[47] = rate;
  } else {
    T0 = $[32];
    T1 = $[33];
    T2 = $[34];
    T3 = $[35];
    loadingSkeleton = $[36];
    rate_limit = $[37];
    rate_limit_period = $[38];
    t12 = $[39];
    t13 = $[40];
    t14 = $[41];
    t15 = $[42];
    t16 = $[43];
    t17 = $[44];
    t18 = $[45];
    t19 = $[46];
    rate = $[47];
  }
  let t20;
  if ($[78] !== T0 || $[79] !== t12 || $[80] !== t13 || $[81] !== t14) {
    t20 = <T0 p={t12}>{t13}{t14}</T0>;
    $[78] = T0;
    $[79] = t12;
    $[80] = t13;
    $[81] = t14;
    $[82] = t20;
  } else {
    t20 = $[82];
  }
  let t21;
  if ($[83] !== T1 || $[84] !== t15 || $[85] !== t16 || $[86] !== t17 || $[87] !== t20) {
    t21 = <T1 w={t15} grid={t16}>{t17}{t20}</T1>;
    $[83] = T1;
    $[84] = t15;
    $[85] = t16;
    $[86] = t17;
    $[87] = t20;
    $[88] = t21;
  } else {
    t21 = $[88];
  }
  let t22;
  if ($[89] !== T2 || $[90] !== t18 || $[91] !== t21) {
    t22 = <T2 m={t18}>{t21}</T2>;
    $[89] = T2;
    $[90] = t18;
    $[91] = t21;
    $[92] = t22;
  } else {
    t22 = $[92];
  }
  const T4 = Div;
  const t23 = 100;
  let t24;
  let t25;
  if ($[93] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = {
      y: 6
    };
    t25 = {
      columns: 3,
      gap: 6
    };
    $[93] = t24;
    $[94] = t25;
  } else {
    t24 = $[93];
    t25 = $[94];
  }
  let t26;
  if ($[95] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = {
      y: 3,
      x: 4
    };
    $[95] = t26;
  } else {
    t26 = $[95];
  }
  let t27;
  let t28;
  let t29;
  if ($[96] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = {
      align: "center",
      gap: 1
    };
    t28 = {
      b: 2
    };
    t29 = <Text semi={true}>Pending events</Text>;
    $[96] = t27;
    $[97] = t28;
    $[98] = t29;
  } else {
    t27 = $[96];
    t28 = $[97];
    t29 = $[98];
  }
  let t30;
  if ($[99] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <Div flex={t27} m={t28}>{t29}<Tooltip tooltip="The number of events currently pending delivery."><Icon muted={true} icon="info" /></Tooltip></Div>;
    $[99] = t30;
  } else {
    t30 = $[99];
  }
  let t31;
  if ($[100] !== loadingSkeleton || $[101] !== total_count_pending) {
    t31 = <StyledCard p={t26}>{t30}{total_count_pending === undefined ? loadingSkeleton : <><Text semi={true} center={true} size="3xl"><NumberFormat renderText={_temp6} displayType="text" value={total_count_pending || 0} thousandSeparator="," /></Text></>}</StyledCard>;
    $[100] = loadingSkeleton;
    $[101] = total_count_pending;
    $[102] = t31;
  } else {
    t31 = $[102];
  }
  const T5 = StyledCard;
  let t32;
  if ($[103] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = {
      y: 3,
      x: 4
    };
    $[103] = t32;
  } else {
    t32 = $[103];
  }
  let t33;
  let t34;
  let t35;
  if ($[104] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = {
      align: "center",
      gap: 1
    };
    t34 = {
      b: 2
    };
    t35 = <Text semi={true}>Highest delay</Text>;
    $[104] = t33;
    $[105] = t34;
    $[106] = t35;
  } else {
    t33 = $[104];
    t34 = $[105];
    t35 = $[106];
  }
  let t36;
  if ($[107] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = <Div flex={t33} m={t34}>{t35}<Tooltip tooltip="The longest amount of time an event has been waiting to be delivered to this destination."><Icon muted={true} icon="info" /></Tooltip></Div>;
    $[107] = t36;
  } else {
    t36 = $[107];
  }
  const t37 = oldest_attempt_date === undefined ? loadingSkeleton : <><Text semi={true} center={true} size="3xl">{typeof oldest_attempt_date === "number" && oldest_attempt_date > 0 ? formatDistanceToNow(new Date(now.getTime() - oldest_attempt_date * 60 * 1000)) : "\xD8"}</Text></>;
  let t38;
  if ($[108] !== T5 || $[109] !== t32 || $[110] !== t36 || $[111] !== t37) {
    t38 = <T5 p={t32}>{t36}{t37}</T5>;
    $[108] = T5;
    $[109] = t32;
    $[110] = t36;
    $[111] = t37;
    $[112] = t38;
  } else {
    t38 = $[112];
  }
  let t39;
  if ($[113] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = {
      y: 3,
      x: 4
    };
    $[113] = t39;
  } else {
    t39 = $[113];
  }
  let t40;
  if ($[114] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = {
      justify: "space-between",
      align: "center"
    };
    $[114] = t40;
  } else {
    t40 = $[114];
  }
  let t41;
  let t42;
  let t43;
  if ($[115] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = {
      align: "center",
      gap: 1
    };
    t42 = {
      b: 2
    };
    t43 = <Text semi={true}>Max delivery rate</Text>;
    $[115] = t41;
    $[116] = t42;
    $[117] = t43;
  } else {
    t41 = $[115];
    t42 = $[116];
    t43 = $[117];
  }
  let t44;
  if ($[118] !== rate_limit_period) {
    t44 = <Text muted={true}>{rate_limit_period}</Text>;
    $[118] = rate_limit_period;
    $[119] = t44;
  } else {
    t44 = $[119];
  }
  let t45;
  if ($[120] === Symbol.for("react.memo_cache_sentinel")) {
    t45 = <Tooltip tooltip="The maximum rate at which events can be delivered to this destination."><Icon muted={true} icon="info" /></Tooltip>;
    $[120] = t45;
  } else {
    t45 = $[120];
  }
  let t46;
  if ($[121] !== t44) {
    t46 = <Div flex={t41} m={t42}>{t43}{t44}{t45}</Div>;
    $[121] = t44;
    $[122] = t46;
  } else {
    t46 = $[122];
  }
  let t47;
  if ($[123] !== mutateIssue || $[124] !== updateRefreshKey) {
    t47 = () => {
      mutateIssue();
      updateRefreshKey();
    };
    $[123] = mutateIssue;
    $[124] = updateRefreshKey;
    $[125] = t47;
  } else {
    t47 = $[125];
  }
  let t48;
  if ($[126] !== issue.data.destination || $[127] !== subscription || $[128] !== t47) {
    t48 = <Dropdown placement="bottom-end" label="Edit" icon="edit" ghost={true} renderToggle={_temp7} expand_icon="chevron_down"><ChangeRateLimit destination={issue.data.destination} subscription={subscription} onUpdate={t47} /></Dropdown>;
    $[126] = issue.data.destination;
    $[127] = subscription;
    $[128] = t47;
    $[129] = t48;
  } else {
    t48 = $[129];
  }
  let t49;
  if ($[130] !== t46 || $[131] !== t48) {
    t49 = <Div flex={t40}>{t46}{t48}</Div>;
    $[130] = t46;
    $[131] = t48;
    $[132] = t49;
  } else {
    t49 = $[132];
  }
  let t50;
  if ($[133] !== rate_limit) {
    t50 = <Text semi={true} center={true} size="3xl">{rate_limit}</Text>;
    $[133] = rate_limit;
    $[134] = t50;
  } else {
    t50 = $[134];
  }
  let t51;
  if ($[135] !== t49 || $[136] !== t50) {
    t51 = <StyledCard p={t39}>{t49}{t50}</StyledCard>;
    $[135] = t49;
    $[136] = t50;
    $[137] = t51;
  } else {
    t51 = $[137];
  }
  let t52;
  if ($[138] !== T4 || $[139] !== t24 || $[140] !== t25 || $[141] !== t31 || $[142] !== t38 || $[143] !== t51) {
    t52 = <T4 w={t23} m={t24} grid={t25}>{t31}{t38}{t51}</T4>;
    $[138] = T4;
    $[139] = t24;
    $[140] = t25;
    $[141] = t31;
    $[142] = t38;
    $[143] = t51;
    $[144] = t52;
  } else {
    t52 = $[144];
  }
  let t53;
  if ($[145] !== onDateRangeChanged) {
    t53 = range => onDateRangeChanged({
      relative: undefined,
      min: range.min,
      max: range.max
    });
    $[145] = onDateRangeChanged;
    $[146] = t53;
  } else {
    t53 = $[146];
  }
  let t54;
  if ($[147] === Symbol.for("react.memo_cache_sentinel")) {
    t54 = {
      columns: 3,
      gap: 6
    };
    $[147] = t54;
  } else {
    t54 = $[147];
  }
  let t55;
  if ($[148] !== backpressure_charts) {
    t55 = <Div grid={t54}>{Object.values(backpressure_charts)}</Div>;
    $[148] = backpressure_charts;
    $[149] = t55;
  } else {
    t55 = $[149];
  }
  let t56;
  if ($[150] !== backpressured_delivery_groups.length || $[151] !== delivery_group_metric_configs || $[152] !== issue.id || $[153] !== refresh_key) {
    t56 = backpressured_delivery_groups.length > 0 && <Div m={{
      t: 8
    }}><Div flex={{
        align: "center",
        justify: "space-between",
        gap: 4
      }} m={{
        b: 4
      }}><Text bold={true} size="l" m={0} as="h2">Backpressured delivery groups</Text><Button outline={true} to={`/issues/${issue.id}/delivery-groups`}>View impacted groups</Button></Div><Div grid={{
        columns: 2,
        gap: 6
      }}><DeliveryGroupMetricChart key={`delivery-group-pending-${refresh_key}`} force_legend={true} chart_type="line" metric="events_pending_count" configs={delivery_group_metric_configs} height={250} /><DeliveryGroupMetricChart key={`delivery-group-oldest-${refresh_key}`} force_legend={true} chart_type="line" metric="events_oldest_pending" configs={delivery_group_metric_configs} height={250} /></Div></Div>;
    $[150] = backpressured_delivery_groups.length;
    $[151] = delivery_group_metric_configs;
    $[152] = issue.id;
    $[153] = refresh_key;
    $[154] = t56;
  } else {
    t56 = $[154];
  }
  let t57;
  if ($[155] !== t55 || $[156] !== t56) {
    t57 = <Div>{t55}{t56}</Div>;
    $[155] = t55;
    $[156] = t56;
    $[157] = t57;
  } else {
    t57 = $[157];
  }
  let t58;
  if ($[158] !== t53 || $[159] !== t57) {
    t58 = <ChartInteractionGroup onTimeRangeSelected={t53}>{t57}</ChartInteractionGroup>;
    $[158] = t53;
    $[159] = t57;
    $[160] = t58;
  } else {
    t58 = $[160];
  }
  let t59;
  if ($[161] !== T3 || $[162] !== t19 || $[163] !== t22 || $[164] !== t52 || $[165] !== t58) {
    t59 = <T3>{t19}{t22}{t52}{t58}</T3>;
    $[161] = T3;
    $[162] = t19;
    $[163] = t22;
    $[164] = t52;
    $[165] = t58;
    $[166] = t59;
  } else {
    t59 = $[166];
  }
  return t59;
};
export default BackpressureIssue;
function _temp6(v) {
  return v;
}
function _temp7(open, toggle) {
  return <Button icon="edit" active={open} ghost={true} onClick={() => toggle(!open)}>Edit</Button>;
}